// tracking_links_utils.js - Shared utilities for tracking links functionality

import { validate_link } from './link_validations';
import {
    dispatchDistributionUpdateToast,
    storeDistributionUpdateFlash
} from '../shared/distribution_update_feedback';

// Make validate_link available globally for Alpine template expressions
window.validate_link = validate_link;

// Common utility functions for tracking links
export const TrackingLinksUtils = {
    // Initialize common data structure
    createInitialData() {
        return {
            distribution_links: [],
            all_distribution_platforms: [],
            distribution_links_truth: [],
            add_more_options: [],
            linkSelector: null,
            selectorVisible: false,
            distributionLinkSelector: undefined,
            existing_links_to_delete: [],
            error_text: "",
            is_submitting: false
        };
    },

    // Create arrays from distribution links data
    createArrays(data, distribution_links, all_distribution_platforms) {

        // Add custom redirect link to the beginning of the array
        all_distribution_platforms.unshift({ name: "Custom", pattern: "custom_url" });


        // Loop through all distribution platforms and add them to the distribution_links_truth array
        for (let i = 0; i < all_distribution_platforms.length; i++) {
            const platform = all_distribution_platforms[i];
            if (distribution_links.hasOwnProperty(platform.pattern) &&
                distribution_links[platform.pattern] !== "") {
                if (platform.name === "Custom") {
                    data.distribution_links_truth.push({
                        name: platform.name,
                        pattern: platform.pattern,
                        url: distribution_links[platform.pattern].url,
                        title: distribution_links[platform.pattern].title
                    });
                } else {
                    data.distribution_links_truth.push({
                        name: platform.name,
                        pattern: platform.pattern,
                        url: distribution_links[platform.pattern]
                    });
                }
            } else {
                data.add_more_options.push({
                    name: platform.name,
                    pattern: platform.pattern
                });
            }
        }
    },

    // Build options list for dropdown
    buildOptionsList(add_more_options) {
        const linkSelector = document.querySelector("#link-selector");
        if (!linkSelector) return;

        linkSelector.innerHTML = `<option value="">Select a listening app</option>`;
        for (let i = 0; i < add_more_options.length; i++) {
            const option = add_more_options[i];
            linkSelector.innerHTML += `<option value="${option.pattern}">${option.name}</option>`;
        }
    },

    // Sync options with TomSelect
    syncOptions(selectorVisible, distributionLinkSelector) {
        if (selectorVisible && distributionLinkSelector) {
            distributionLinkSelector.sync();
        }
    },

    // Add to options list
    addToOptionsList(add_more_options, name, pattern) {
        add_more_options.push({ name, pattern });
    },

    // Remove from options list
    removeFromOptionsList(add_more_options, pattern) {
        for (let i = 0; i < add_more_options.length; i++) {
            if (add_more_options[i].pattern === pattern) {
                add_more_options.splice(i, 1);
                break;
            }
        }
    },

    // Delete link functionality
    deleteLink(data, name, pattern) {
        const el = document.querySelector(`#${pattern}`);
        if (el) el.remove();

        for (let i = 0; i < data.distribution_links_truth.length; i++) {
            if (data.distribution_links_truth[i].pattern === pattern) {
                data.distribution_links_truth.splice(i, 1);
                break;
            }
        }

        if (data.distribution_links &&
            data.distribution_links.hasOwnProperty(pattern) &&
            !data.existing_links_to_delete.includes(pattern)) {
            data.existing_links_to_delete.push(pattern);
        }

        this.addToOptionsList(data.add_more_options, name, pattern);
        this.buildOptionsList(data.add_more_options);
    },

    // Handle add more functionality
    handleAddMore(data, validateLinks = true) {
        if (validateLinks) {
            const linkElements = document.querySelectorAll(".ql-link-el");
            for (let i = 0; i < linkElements.length; i++) {
                const linkElement = linkElements[i];
                const pattern = linkElement.id;
                const url = document.querySelector(`#${pattern}-url`).value;
                if (validate_link(pattern, url) === false) {
                    alert('Please fix the issues with the links before adding more.');
                    return;
                }
            }
        }

        const linkSelector = document.querySelector("#link-selector");
        if (!linkSelector) return;

        const handleItemAdd = (value, item) => {
            data.distributionLinkSelector.clear();
            data.distributionLinkSelector.destroy();
            data.selectorVisible = false;
            linkSelector.classList.add("hidden");
            this.createLinkElement(value, item, data);
        };

        const handleBlur = () => {
            data.distributionLinkSelector.clear();
            data.distributionLinkSelector.destroy();
            data.selectorVisible = false;
            linkSelector.classList.add("hidden");
        };

        linkSelector.classList.remove("hidden");
        data.distributionLinkSelector = new TomSelect("#link-selector", { create: false });
        data.selectorVisible = true;
        data.distributionLinkSelector.on("item_add", handleItemAdd);
        data.distributionLinkSelector.on("blur", handleBlur);
    },

    // Create link element
    createLinkElement(value, item, data) {
        const linksContainer = document.querySelector("#links-container");
        const newDiv = document.createElement("div");
        linksContainer.appendChild(newDiv);

        if (item.textContent === "Custom") {
            newDiv.outerHTML = `
        <div id="${value}" class="border rounded p-4 ql-link-el">
          <div class="flex space-x-4 items-center">
            <div class="w-12 h-12">
              <img src="https://files.cohostpodcasting.com/static/${value}_image.svg" alt="${item.textContent} Logo">
            </div>
            <div class="flex flex-col flex-1 space-y-2">
              <div class="font-bold text-sm">${item.textContent}</div>
              <div class="flex space-x-2 items-center">
                <div class="flex space-x-2 w-full">
                    <div class="relative rounded-md shadow-sm w-full">
                        <input @keyup="$event.target.value !== '' ? $event.target.nextElementSibling.classList.remove('hidden') : $event.target.nextElementSibling.classList.add('hidden')" id="${value}-title" type="text" class="block pr-10 h-9 w-full rounded border-gray-300 text-sm" placeholder="Enter the name to be displayed" value="">
                        <div class="hidden absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C6.47967 21.994 2.00606 17.5204 2 12V11.8C2.10993 6.30455 6.63459 1.92797 12.1307 2.0009C17.6268 2.07382 22.0337 6.5689 21.9978 12.0654C21.9619 17.5618 17.4966 21.9989 12 22ZM7.41 11.59L6 13L10 17L18 9.00002L16.59 7.58002L10 14.17L7.41 11.59Z" fill="#059669"/>
                          </svg>
                        </div>
                    </div>
                    <div class="relative rounded-md shadow-sm w-full"
                         x-data="{ url: '', is_valid: null }"
                         x-init="$watch('url', value => { is_valid = value === '' ? null : validate_link('${value}', value) })">
                        <input
                          x-model="url"
                          id="${value}-url"
                          type="text"
                          class="block pr-10 h-9 w-full rounded border-gray-300 text-sm"
                          placeholder="Copy and paste your custom link"
                          value="">
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" x-show="is_valid === true">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C6.47967 21.994 2.00606 17.5204 2 12V11.8C2.10993 6.30455 6.63459 1.92797 12.1307 2.0009C17.6268 2.07382 22.0337 6.5689 21.9978 12.0654C21.9619 17.5618 17.4966 21.9989 12 22ZM7.41 11.59L6 13L10 17L18 9.00002L16.59 7.58002L10 14.17L7.41 11.59Z" fill="#059669"/>
                          </svg>
                        </div>
                        <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" x-show="is_valid === false">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#DC2626"/>
                            <path d="M15 9L9 15M9 9l6 6" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                          </svg>
                        </div>
                    </div>
                </div>
                <svg class="cursor-pointer" @click="delete_link('${item.textContent}','${value}')" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6H5H21" stroke="#8B5CF6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="#8B5CF6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M14 11V17" stroke="#8B5CF6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M10 11V17" stroke="#8B5CF6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      `;
        } else {
            newDiv.outerHTML = `
        <div id="${value}" class="border rounded p-4 ql-link-el">
          <div class="flex space-x-4 items-center">
            <div class="w-12 h-12">
              <img src="https://files.cohostpodcasting.com/static/${value}_image.svg" alt="${item.textContent} Logo">
            </div>
            <div class="flex flex-col flex-1 space-y-2">
              <div class="font-bold text-sm">${item.textContent}</div>
              <div class="flex space-x-2 items-center">
                <div class="relative rounded-md shadow-sm w-full"
                  x-data="{ url: '', is_valid: null }"
                  x-init="$watch('url', value => { is_valid = value === '' ? null : validate_link('${value}', value) })">
                  <input
                    x-model="url"
                    id="${value}-url"
                    type="text"
                    class="block pr-10 h-9 w-full rounded border-gray-300 text-sm"
                    placeholder="Copy and paste your ${item.textContent} show link"
                  >
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" x-show="is_valid === true">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 22C6.47967 21.994 2.00606 17.5204 2 12V11.8C2.10993 6.30455 6.63459 1.92797 12.1307 2.0009C17.6268 2.07382 22.0337 6.5689 21.9978 12.0654C21.9619 17.5618 17.4966 21.9989 12 22ZM7.41 11.59L6 13L10 17L18 9.00002L16.59 7.58002L10 14.17L7.41 11.59Z" fill="#059669"/>
                    </svg>
                  </div>
                  <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" x-show="is_valid === false">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z" fill="#DC2626"/>
                      <path d="M15 9L9 15M9 9l6 6" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </div>
                </div>
                <svg class="cursor-pointer" @click="delete_link('${item.textContent}','${value}')" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6H5H21" stroke="#8B5CF6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8 6V4C8 3.47 8.21 2.96 8.59 2.59C8.96 2.21 9.47 2 10 2H14C14.53 2 15.04 2.21 15.41 2.59C15.79 2.96 16 3.47 16 4V6M19 6V20C19 20.53 18.79 21.04 18.41 21.41C18.04 21.79 17.53 22 17 22H7C6.47 22 5.96 21.79 5.59 21.41C5.21 21.04 5 20.53 5 20V6H19Z" stroke="#8B5CF6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M14 11V17M10 11V17" stroke="#8B5CF6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>
          </div>
        </div>
      `;
        }

        this.removeFromOptionsList(data.add_more_options, value);
        data.distribution_links_truth.push({
            name: item.textContent,
            pattern: value,
            url: ""
        });
    },

    // Submit functionality with validation
    submit(data, options = {}) {
        const {
            authenticity_token,
            batch_update_url,
            link_type,
            episode_id,
            redirect_url,
            toast_text,
            tracking_link_ids = [],
            checkEmptyUrls = true
        } = options;

        if (data.is_submitting) return; // Prevent double submission

        data.is_submitting = true;
        const linkElements = document.querySelectorAll(".ql-link-el");
        let submissionData = {};
        let body = {};
        let hasEmptyUrls = false;
        let redirectUrl = null;

        if (data.redirect_only) {
            if (linkElements.length === 0) {
                alert('Please add a listening app link before saving.');
                data.is_submitting = false;
                return;
            }

            const linkElement = linkElements[0];
            const pattern = linkElement.id;
            const urlInput = document.querySelector(`#${pattern}-url`);
            const url = urlInput ? urlInput.value : '';

            if (!url || url.trim() === '') {
                alert('Please enter a valid URL for the redirect link.');
                data.is_submitting = false;
                return;
            }

            if (validate_link(pattern, url) === false) {
                alert('Please enter a valid URL for this platform.');
                data.is_submitting = false;
                return;
            }

            if (pattern === "custom_url") {
                const titleElement = document.querySelector(`#${pattern}-title`);
                const title = titleElement ? titleElement.value : "";
                if (!title.trim()) {
                    alert('Please enter a title for the custom link.');
                    data.is_submitting = false;
                    return;
                }
                submissionData[pattern] = { url: url, title: title };
            } else {
                submissionData[pattern] = url;
            }

            redirectUrl = url;
            submissionData.redirect_url = redirectUrl;
        } else {
            // Normal mode - process all links as usual
            for (let i = 0; i < linkElements.length; i++) {
                const linkElement = linkElements[i];
                const pattern = linkElement.id;
                let url = document.querySelector(`#${pattern}-url`).value;

                // Check for empty URLs if enabled
                if (checkEmptyUrls && (!url || url.trim() === '')) {
                    hasEmptyUrls = true;
                    continue; // Skip this link but continue checking others
                }

                if (pattern === "custom_url") {
                    const titleElement = document.querySelector(`#${pattern}-title`);
                    const title = titleElement ? titleElement.value : "";
                    submissionData[pattern] = { url: url, title: title };
                } else {
                    submissionData[pattern] = url;
                }

                if (validate_link(pattern, url) === false) {
                    alert('Please fix the issues with the links before submitting.');
                    data.is_submitting = false;
                    return;
                }
            }

            // Check if any URLs were empty
            if (checkEmptyUrls && hasEmptyUrls) {
                alert('One or more links are empty. Please fill in all link URLs before saving.');
                data.is_submitting = false;
                return;
            }
        }

        body["authenticity_token"] = authenticity_token;
        body["distribution_links"] = submissionData;

        if (tracking_link_ids.length > 0) {
            body["tracking_link_ids"] = tracking_link_ids;
        }

        if (link_type !== 'show') {
            body["episode_id"] = episode_id;
        }

        for (let i = 0; i < data.existing_links_to_delete.length; i++) {
            if (submissionData.hasOwnProperty(data.existing_links_to_delete[i]) === false) {
                submissionData[data.existing_links_to_delete[i]] = "";
            }
        }

        fetch(batch_update_url, {
            headers: {
                "Content-Type": "application/json"
            },
            method: "POST",
            body: JSON.stringify(body)
        })
            .then(response => response.json())
            .then(responseData => {
                if (redirect_url) {
                    // Keep the next page honest when the save worked but refresh queueing did not.
                    storeDistributionUpdateFlash(responseData, 'Your tracking link(s) have been created!');
                    window.location.href = redirect_url;
                } else {
                    dispatchDistributionUpdateToast(responseData, toast_text || "Tracking links have been changed!");
                }
                data.is_submitting = false;
            })
            .catch((error) => {
                console.error('Error updating tracking link:', error);
                alert('An error occurred while updating the tracking link. Please try again.');
                data.is_submitting = false;
            });
    },

    // Error handling
    setErrorMessage(all_distribution_platforms, pattern) {
        for (let i = 0; i < all_distribution_platforms.length; i++) {
            const item = all_distribution_platforms[i];
            if (item.pattern === pattern) {
                return `Please enter a valid ${item.name} link.`;
            }
        }
        return '';
    },

    clearErrorMessage(data) {
        data.error_text = "";
    }
};

// Export for use in other modules
export default TrackingLinksUtils; 
